import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb } from 'lucide-react';

interface LoadingPanelProps {
  title: string;
  tips?: string[];
  /** Milliseconds between tip changes. Defaults to 4500. */
  intervalMs?: number;
}

export const LoadingPanel: React.FC<LoadingPanelProps> = ({
  title,
  tips = [],
  intervalMs = 4500,
}) => {
  const { t } = useTranslation('dashboard');
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (tips.length <= 1) return;
    const interval = setInterval(() => {
      setIdx((i) => (i + 1) % tips.length);
    }, intervalMs);
    return () => clearInterval(interval);
  }, [tips.length, intervalMs]);

  return (
    <div className="flex flex-1 items-center justify-center px-4 py-6">
      <div className="flex flex-col items-center text-center w-full max-w-md gap-7">
        <div className="relative">
          {/* Soft halo behind the logo to give it more visual weight. */}
          <span
            aria-hidden
            className="absolute inset-0 -m-4 rounded-full bg-purple/10 blur-2xl"
          />
          <img
            src="/logo/light-logo.png"
            alt="Invirtus"
            width={120}
            height={120}
            className="relative animate-[spin_3s_linear_infinite]"
          />
        </div>

        <h4 className="font-heading text-dark-invirtus tracking-tight">{title}</h4>

        {tips.length > 0 && (
          <div className="w-full pt-6 border-t border-gray-100">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Lightbulb className="h-3.5 w-3.5 text-purple" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-heading">
                {t('create_job_modal.did_you_know')}
              </span>
            </div>
            <div
              className="relative min-h-[60px]"
              aria-live="polite"
              aria-atomic="true"
            >
              {tips.map((tip, i) => (
                <p
                  key={i}
                  className={[
                    'absolute inset-0 text-sm text-gray-700 font-body leading-relaxed transition-all duration-600',
                    i === idx
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-2 pointer-events-none',
                  ].join(' ')}
                >
                  {tip}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
